import type {
  BrowserHistoryBuildOptions,
  History,
  LocationDescriptor,
  LocationDescriptorObject,
  LocationListener,
  TransitionPromptHook,
} from 'history';
import { createBrowserHistory as _createBrowserHistory } from 'history';

export type State = {
  fromApp?: boolean;
  [key: string]: unknown;
};

export class AppHistory implements History<State> {
  _history: History<State>;

  constructor(opts?: BrowserHistoryBuildOptions) {
    this._history = _createBrowserHistory<State>(opts);
  }

  get length() {
    return this._history.length;
  }

  get action() {
    return this._history.action;
  }

  get location() {
    return this._history.location;
  }

  createHref(to: LocationDescriptorObject<State>) {
    return this._history.createHref(to);
  }

  push = (to: string | LocationDescriptor<State>, state?: State) => {
    const newState = mergeState(to, state);

    if (typeof to === 'string') {
      this._history.push(to, newState);
    } else {
      this._history.push({ ...to, state: newState });
    }
  };

  replace = (to: string | LocationDescriptor<State>, state?: State) => {
    const newState = mergeState(to, state);

    if (typeof to === 'string') {
      this._history.replace(to, newState);
    } else {
      this._history.replace({ ...to, state: newState });
    }
  };

  go = (n: number) => {
    return this._history.go(n);
  };

  goBack = () => {
    const { fromApp } = this.location?.state || {};
    if (!fromApp) {
      this.replace('/');
      return;
    }
    return this._history.goBack();
  };

  goForward = () => {
    return this._history.goForward();
  };

  block(prompt?: boolean | string | TransitionPromptHook<State>) {
    return this._history.block(prompt);
  }

  listen(listener: LocationListener<State>) {
    return this._history.listen(listener);
  }
}

function mergeState(to: string | LocationDescriptor<State>, manualState: State | undefined) {
  const oState = typeof to === 'string' ? {} : to.state || {};
  return { ...oState, ...manualState, fromApp: true };
}

export function createBrowserHistory(opts?: BrowserHistoryBuildOptions) {
  return new AppHistory(opts);
}
